interface DemoNavProps {
  /** Accessible name for the inline-START button. */
  prevLabel: string;
  /** Accessible name for the inline-END button. */
  nextLabel: string;
  /** Called with the index to move to. Wraps at both ends. */
  onStep: (index: number) => void;
  activeIndex: number;
  count: number;
}

/**
 * Previous / next controls flanking the app-shell demo.
 *
 * THESE ARE THE ONLY REAL CONTROLS ON THE DEMO. Everything inside the shell is a
 * <span>: it is a mockup, and a <button> in there would be a keyboard stop that
 * does nothing. These are the opposite — real buttons, focusable, labelled, and
 * the first way a keyboard user can drive the carousel at all.
 *
 * DIRECTION IS HANDLED LOGICALLY, IN THREE SEPARATE PLACES, and they are not the
 * same decision:
 *
 *   1. POSITION — CSS `inset-inline-start` / `-end`. The prev button sits at the
 *      start edge, which the browser resolves to left in LTR and right in RTL.
 *      Nothing in the markup or the JS knows which.
 *   2. GLYPH — a chevron pointing toward that same edge, mirrored under RTL in
 *      CSS. It has to move WITH the button: a prev button on the right of an
 *      Arabic layout pointing left would be an arrow contradicting its own
 *      position, which is the classic unmirrored-icon bug.
 *   3. MEANING — `prev` always means "the tab before this one", in both
 *      directions. This is the part that must NOT flip: reading order flips, the
 *      sequence does not.
 *
 * Wrapping rather than disabling at the ends: the carousel itself wraps, so a
 * disabled button would contradict what the thing does on its own, and a control
 * that is dead a fifth of the time reads as broken rather than as bounded.
 */
export default function DemoNav({
  prevLabel,
  nextLabel,
  onStep,
  activeIndex,
  count,
}: DemoNavProps) {
  const go = (delta: number) => onStep((activeIndex + delta + count) % count);

  return (
    <>
      <button
        type="button"
        className="mkt-hero-tw__navbtn mkt-hero-tw__navbtn--prev"
        aria-label={prevLabel}
        onClick={() => go(-1)}
      >
        <Chevron />
      </button>
      <button
        type="button"
        className="mkt-hero-tw__navbtn mkt-hero-tw__navbtn--next"
        aria-label={nextLabel}
        onClick={() => go(1)}
      >
        <Chevron />
      </button>
    </>
  );
}

/* One glyph for both buttons, pointed by CSS. Drawing two would be two things to
   keep mirrored instead of one. */
function Chevron() {
  return (
    <svg
      className="mkt-hero-tw__navchev"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M9 5l7 7-7 7" />
    </svg>
  );
}
